import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Video,
  Radio,
  Camera,
  Users,
  Tv,
  Sparkles,
  Play,
  ChevronDown,
  Check,
  Clapperboard,
  ScanEye,
} from 'lucide-react';

/**
 * DraftHomePage — internal preview at /drafthomepage
 * Cinematic, scroll-driven story that spells out exactly what Elevated
 * Roping Media offers so ropers stop guessing what the site is.
 *
 * NOTE: Do NOT wire this into the main LandingPage. This route is public
 * and unlisted; share the URL with the team for feedback only.
 */

// Reveal-on-scroll wrapper — pure IntersectionObserver, no libs.
const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.15 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const DraftHomePage = () => {
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const pillars = [
    {
      icon: Radio,
      tag: '01',
      title: 'Live Event Streams',
      copy: 'The events we film — team roping, barrel racing, steer riding, wild horses and more — streamed live. Watch every run from your phone, your couch, or pair a code to your TV. Never miss your kid, your partner, or your paycheck ride.',
      cta: 'Live & Events',
      href: '/events',
    },
    {
      icon: Video,
      tag: '02',
      title: 'Coach Critiques',
      copy: 'Send us a run or a race. A real coach breaks it down on video — voice-over, lines drawn on your form, drills to work on before the next weekend.',
      cta: 'Coaches / Critiques',
      href: '/coaches',
    },
    {
      icon: Camera,
      tag: '03',
      title: 'Event Photos & Video',
      copy: 'Every run, race and ride we film — photos and clips tagged to the event. Preview free, download the ones you want to keep forever.',
      cta: 'Event Gallery',
      href: '/gallery',
    },
    {
      icon: Users,
      tag: '04',
      title: 'Training Library',
      copy: 'Free clinics, drill breakdowns, and community posts across disciplines. Learn between events — not by guessing on YouTube.',
      cta: 'Training Library',
      href: '/library',
    },
  ];

  const faqs = [
    {
      q: 'What is Elevated Roping Media, in one sentence?',
      a: 'A private membership for the rodeo community — live event streams (team roping, barrel racing, steer riding, wild horses and more), critiques from real coaches, photos & videos of your rides, and a training library.',
    },
    {
      q: 'Do I need to subscribe just to look around?',
      a: 'The landing pages, event schedule, and sponsor pages are open. Everything else — live feeds, critiques, the training library, the marketplace — sits behind a $25/mo (or $240/yr) membership.',
    },
    {
      q: 'What disciplines do you cover?',
      a: 'Team roping, barrel racing, steer riding, wild horse events, breakaway, tie-down — anything happening on the arena floor at the events we film.',
    },
    {
      q: 'How do I watch live on TV?',
      a: 'Open elevatedarena.com/tv on your Smart TV or browser, get a 6-digit code, type it into your phone once — the TV stays paired until you sign out.',
    },
    {
      q: 'What happens if I cancel?',
      a: 'You keep access through the end of the billing period, then the account goes read-only. Your critique history and gallery purchases stay yours forever.',
    },
  ];

  // Hero scenes — alternate between the two verified fast-loading rodeo
  // photos (Tucson steer roping + Unsplash rodeo arena), with different
  // crops per scene so each frame feels visually distinct.
  const tucson =
    'https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/cx8ya1lt_Tucson%20Rodeo.jpg';
  const arenaWide =
    'https://images.unsplash.com/photo-1760041870925-0a6ed8220ce4?auto=format&fit=crop&w=2400&q=80';
  const heroScenes = [
    {
      bg: tucson,
      filter: 'none',
      bgPos: 'center center',
      pre: 'Live · Team Roping',
      lead: 'Every event.',
      accent: 'Live from the arena.',
    },
    {
      bg: arenaWide,
      filter: 'saturate(1.15) contrast(1.05)',
      bgPos: 'center center',
      pre: 'Live · Barrel Racing',
      lead: 'Every run.',
      accent: 'On every screen.',
    },
    {
      bg: tucson,
      filter: 'grayscale(0.5) contrast(1.15) brightness(0.95)',
      bgPos: 'left center',
      pre: 'Coached',
      lead: 'Then broken down.',
      accent: 'Marked in ink.',
    },
    {
      bg: arenaWide,
      filter: 'sepia(0.35) saturate(1.3) brightness(0.9)',
      bgPos: 'right center',
      pre: 'Stored',
      lead: 'Your photos, waiting.',
      accent: 'The moment you cross the line.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0f0b08] text-[#f5efe6]">
      {/* Draft-mode banner */}
      <div className="sticky top-0 z-50 bg-primary/90 backdrop-blur border-b border-primary text-primary-foreground text-center py-2 text-xs uppercase tracking-widest font-semibold">
        <span className="inline-flex items-center gap-2">
          <Sparkles className="w-3 h-3" />
          Draft homepage — internal preview
          <span className="hidden sm:inline text-primary-foreground/80 normal-case tracking-normal font-normal">
            (share with the team, not live yet)
          </span>
        </span>
      </div>

      {/* ==================== HERO (scroll-driven scene swap) ==================== */}
      <HeroScroller scenes={heroScenes} />

      {/* ==================== DISCIPLINES STRIP ==================== */}
      <section className="relative py-6 border-y border-[#c8a672]/15 bg-[#0f0b08] overflow-hidden">
        <div className="flex whitespace-nowrap gap-8 sm:gap-12 marquee-track uppercase text-xs sm:text-sm tracking-[0.4em] text-[#c8a672]/80 font-medium">
          {Array.from({ length: 3 }).map((_, loop) => (
            <div key={loop} className="flex items-center gap-8 sm:gap-12 shrink-0">
              {[
                'Team Roping',
                'Barrel Racing',
                'Steer Riding',
                'Wild Horses',
                'Breakaway',
                'Tie-Down',
                'Clinics',
                'Futurities',
              ].map((d) => (
                <span key={`${loop}-${d}`} className="inline-flex items-center gap-3">
                  {d}
                  <span className="w-1.5 h-1.5 rounded-full bg-[#c8a672]/40" />
                </span>
              ))}
            </div>
          ))}
        </div>
        <style>{`
          @keyframes marquee-slide {
            from { transform: translateX(0); }
            to   { transform: translateX(-33.333%); }
          }
          .marquee-track {
            width: max-content;
            animation: marquee-slide 40s linear infinite;
          }
        `}</style>
      </section>

      {/* ==================== WHAT IS THIS ==================== */}
      <section className="py-32 px-6 relative">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="uppercase tracking-[0.4em] text-xs text-[#c8a672] mb-6">
              Wait — what actually is this?
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl leading-tight mb-10">
              We <span className="text-[#c8a672]">stream</span> the events.<br />
              We coach the rides.<br />
              We store the photos.
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <div className="max-w-3xl text-lg sm:text-xl text-[#d9ccb8] leading-relaxed space-y-6">
              <p>
                Every event we cover — team ropings, barrel races, steer
                riding, wild horses, clinics, futurities — goes out live.
                Multiple cameras, real-time, on your phone or paired to a TV
                so the whole family can watch.
              </p>
              <p>
                After the run, a real coach breaks it down on video —
                voice-over, ink on your form, the drill you should be working
                on before the next weekend. Photos and clips of every ride are
                tagged and waiting in the gallery.
              </p>
              <p className="text-[#c8a672] font-medium">
                One membership. Every stream, every breakdown, every photo —
                for the price of a couple of gas fills a month.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ==================== 3D SCROLL SEQUENCE ==================== */}
      <ScrollSequence />

      {/* ==================== FOUR PILLARS (sticky story) ==================== */}
      <section className="relative bg-gradient-to-b from-[#0f0b08] via-[#1a120b] to-[#0f0b08]">
        <div className="max-w-6xl mx-auto px-6 pt-20 pb-32">
          <Reveal>
            <p className="uppercase tracking-[0.4em] text-xs text-[#c8a672] mb-6">
              What you get
            </p>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl leading-tight mb-20">
              Four things.<br />
              Under one roof.
            </h2>
          </Reveal>

          <div className="space-y-24">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              const flip = i % 2 === 1;
              return (
                <Reveal key={p.tag} delay={i * 60}>
                  <div
                    className={`grid md:grid-cols-12 gap-8 items-center ${
                      flip ? 'md:[&>*:first-child]:order-2' : ''
                    }`}
                  >
                    <div className="md:col-span-5">
                      <div className="border border-[#c8a672]/25 rounded-3xl p-10 bg-[#1a120b]/60 backdrop-blur-sm hover:border-[#c8a672]/60 transition-colors">
                        <div className="flex items-center justify-between mb-8">
                          <span className="text-[#c8a672]/50 font-display text-6xl leading-none">
                            {p.tag}
                          </span>
                          <div className="w-14 h-14 rounded-full bg-[#c8a672]/10 flex items-center justify-center border border-[#c8a672]/30">
                            <Icon className="w-6 h-6 text-[#c8a672]" />
                          </div>
                        </div>
                        <h3 className="font-display font-bold uppercase text-2xl sm:text-3xl mb-4 tracking-tight">
                          {p.title}
                        </h3>
                        <p className="text-[#d9ccb8] leading-relaxed mb-8">
                          {p.copy}
                        </p>
                        <Link
                          to={p.href}
                          data-testid={`draft-pillar-${p.tag}`}
                          className="group inline-flex items-center gap-2 text-[#c8a672] font-semibold uppercase tracking-widest text-xs"
                        >
                          {p.cta}
                          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                    <div className="md:col-span-7">
                      <PillarVisual index={i} />
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== THE MOMENT (parallax quote) ==================== */}
      <section className="relative py-40 overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1760041870925-0a6ed8220ce4?auto=format&fit=crop&w=2000&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'grayscale(0.4) contrast(1.1)',
            transform: `translate3d(0, ${(scrollY - 2000) * 0.15}px, 0)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f0b08] via-transparent to-[#0f0b08]" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <blockquote className="font-display italic text-3xl sm:text-5xl lg:text-6xl leading-tight text-[#f5efe6]">
              &ldquo;If it&apos;s on the arena floor,
              <br />
              it&apos;s on your{' '}
              <span className="text-[#c8a672]">screen</span>.&rdquo;
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* ==================== PRICING ==================== */}
      <section className="py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="uppercase tracking-[0.4em] text-xs text-[#c8a672] mb-6 text-center">
              What it costs
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl leading-tight text-center mb-4">
              Membership + credits.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="text-center text-[#d9ccb8] max-w-2xl mx-auto mb-16">
              Membership unlocks the platform. Elevated Credits pay for
              critiques, video downloads, and photo bundles — you only spend
              on what you actually want.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6">
            <Reveal delay={80}>
              <PricingCard
                name="Monthly"
                price="$25"
                cadence="/mo"
                perks={[
                  'Access to every live event feed',
                  'Full training library',
                  'Discounted gallery pricing',
                  'Community + marketplace',
                  'Monthly credits included',
                ]}
                cta="Start Monthly"
              />
            </Reveal>
            <Reveal delay={160}>
              <PricingCard
                name="Annual"
                price="$240"
                cadence="/yr"
                highlight
                subtitle="Two months on the house"
                perks={[
                  'Everything in Monthly',
                  'Two free months vs $25 × 12',
                  'Bigger monthly credit drop',
                  'Priority coach queue',
                  'Save it toward your fall entries',
                ]}
                cta="Start Annual"
              />
            </Reveal>
          </div>

          <Reveal delay={220}>
            <p className="text-center text-xs text-[#c8a672]/70 uppercase tracking-widest mt-10">
              Cancel any time — you keep access through the billing period.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ==================== FAQ ==================== */}
      <section className="py-24 px-6 bg-[#1a120b]/40">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <p className="uppercase tracking-[0.4em] text-xs text-[#c8a672] mb-6">
              Honest answers
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display font-bold uppercase text-4xl sm:text-5xl mb-12">
              The five things ropers ask.
            </h2>
          </Reveal>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <Reveal key={i} delay={i * 60}>
                <details className="group border border-[#c8a672]/20 rounded-2xl bg-[#0f0b08]/40 open:border-[#c8a672]/60 transition-colors">
                  <summary className="cursor-pointer list-none px-6 py-5 flex items-center justify-between gap-4">
                    <span className="font-display font-semibold uppercase tracking-tight text-lg">
                      {f.q}
                    </span>
                    <ChevronDown className="w-5 h-5 text-[#c8a672] group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="px-6 pb-6 text-[#d9ccb8] leading-relaxed">
                    {f.a}
                  </div>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="relative py-40 overflow-hidden text-center">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f0b08] via-[#1a120b] to-[#0f0b08]" />
        <div className="relative max-w-3xl mx-auto px-6">
          <Reveal>
            <h2 className="font-display font-bold uppercase text-5xl sm:text-6xl lg:text-7xl leading-tight mb-8">
              The next roping
              <br />
              is <span className="text-[#c8a672] italic font-light">already</span> on air.
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-[#d9ccb8] text-lg mb-10 max-w-xl mx-auto">
              Grab a membership, tune into the next live feed, and let&apos;s
              get your run in front of a coach before the weekend.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <Link
              to="/signup"
              data-testid="draft-final-cta"
              className="group inline-flex items-center gap-3 bg-[#c8a672] text-[#0f0b08] px-10 py-5 rounded-full font-bold uppercase tracking-widest text-sm hover:bg-[#d9bd88] transition-colors"
            >
              Start Membership
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Footer strip */}
      <footer className="border-t border-[#c8a672]/20 py-8 px-6 text-center text-xs text-[#c8a672]/70">
        <p className="uppercase tracking-[0.3em]">
          Elevated Roping Media · Draft homepage preview · v0.1
        </p>
      </footer>
    </div>
  );
};

// ============ Hero scroller — scroll-driven scene swap ============
// A 300vh tall container with a sticky inner viewport. As the user scrolls
// through, the background photos cross-fade and the headline morphs — like
// the reference site but without hijacking their scroll.
const HeroScroller = ({ scenes }) => {
  const wrapRef = useRef(null);
  const [progress, setProgress] = useState(0); // 0..1 across whole hero

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    let raf = null;
    const compute = () => {
      raf = null;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const p = Math.max(0, Math.min(1, -rect.top / (total || 1)));
      setProgress(p);
    };
    const onScroll = () => {
      if (raf === null) raf = requestAnimationFrame(compute);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    compute();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // Preload images once
  useEffect(() => {
    scenes.forEach((s) => {
      const img = new Image();
      img.src = s.bg;
    });
  }, [scenes]);

  const sceneLen = 1 / scenes.length;
  const activeIndex = Math.min(
    scenes.length - 1,
    Math.floor(progress / sceneLen)
  );
  const localP = Math.min(1, (progress - activeIndex * sceneLen) / sceneLen);

  return (
    <section
      ref={wrapRef}
      className="relative"
      style={{ height: '300vh' }}
      data-testid="hero-scroller"
    >
      <div className="sticky top-0 h-screen overflow-hidden">
        {/* Cross-fading background images */}
        {scenes.map((s, i) => {
          // Tight image crossfade at localP 0.4-0.6, mirroring the text
          let opacity = 0;
          if (i === activeIndex) {
            opacity = localP < 0.4 ? 1 : Math.max(0, 1 - (localP - 0.4) * 5);
          } else if (i === activeIndex + 1) {
            opacity = localP < 0.5 ? 0 : Math.min(1, (localP - 0.5) * 5);
          }
          const scale = 1.1 + (i === activeIndex ? localP * 0.05 : 0);
          return (
            <div
              key={i}
              className="absolute inset-0 will-change-[opacity,transform]"
              style={{
                backgroundImage: `url(${s.bg})`,
                backgroundSize: 'cover',
                backgroundPosition: s.bgPos || 'center',
                filter: s.filter || 'none',
                opacity,
                transform: `scale(${scale})`,
              }}
            />
          );
        })}

        {/* Darkening + grain overlays (constant) */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f0b08]/40 via-[#0f0b08]/70 to-[#0f0b08]" />
        <div
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
          }}
        />

        {/* Scene indicator dots */}
        <div className="absolute top-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {scenes.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all duration-500 ${
                i === activeIndex ? 'w-10 bg-[#c8a672]' : 'w-4 bg-[#c8a672]/25'
              }`}
            />
          ))}
        </div>

        {/* Scene text — all stacked at center, cross-fade */}
        <div className="relative h-full flex flex-col items-center justify-center px-6">
          <div className="relative w-full max-w-5xl mx-auto text-center">
            {/* Stacked scene text (absolutely stacked, clean baton-pass cross-fade) */}
            <div className="relative min-h-[280px] sm:min-h-[360px]">
              {scenes.map((s, i) => {
                // Snap baton-pass: outgoing hidden by localP=0.5,
                // incoming appears sharp at 0.5. Tight window (0.4-0.6).
                let opacity = 0;
                let translateY = 0;
                if (i === activeIndex) {
                  // Fade from localP 0.4 → 0.5
                  opacity = localP < 0.4 ? 1 : Math.max(0, 1 - (localP - 0.4) * 10);
                  translateY = -localP * 24;
                } else if (i === activeIndex + 1) {
                  // Fade from localP 0.5 → 0.6
                  opacity = localP < 0.5 ? 0 : Math.min(1, (localP - 0.5) * 10);
                  translateY = (1 - localP) * 24;
                }
                if (i === scenes.length - 1 && activeIndex === scenes.length - 1) {
                  opacity = 1;
                  translateY = 0;
                }
                return (
                  <div
                    key={s.lead}
                    className="absolute inset-0 flex items-center justify-center"
                    style={{
                      opacity,
                      transform: `translateY(${translateY}px)`,
                      pointerEvents: opacity > 0.5 ? 'auto' : 'none',
                    }}
                  >
                    <div>
                      <p className="uppercase tracking-[0.5em] text-xs text-[#c8a672] mb-6 font-medium">
                        <span className="inline-flex items-center gap-2">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                          </span>
                          {s.pre}
                        </span>
                      </p>
                      <h1 className="font-display font-bold uppercase tracking-tight leading-[0.9] text-5xl sm:text-7xl lg:text-8xl">
                        {s.lead}
                        <br />
                        <span className="text-[#c8a672] italic font-light">{s.accent}</span>
                      </h1>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Fixed sub-copy + CTAs (don't fade) */}
            <p className="max-w-2xl mx-auto text-base sm:text-lg text-[#d9ccb8] leading-relaxed mt-6 mb-8">
              Team roping. Barrel racing. Steer riding. Wild horses. Watch
              every run live — then get your ride broken down by a real
              coach, download the photos, and drill the fix.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/events"
                data-testid="draft-hero-events"
                className="group inline-flex items-center gap-2 bg-[#c8a672] text-[#0f0b08] px-8 py-4 rounded-full font-bold uppercase tracking-widest text-sm hover:bg-[#d9bd88] transition-colors"
              >
                Watch Live
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/signup"
                data-testid="draft-hero-signup"
                className="inline-flex items-center gap-2 border border-[#c8a672]/40 text-[#f5efe6] px-8 py-4 rounded-full font-medium uppercase tracking-widest text-sm hover:border-[#c8a672] transition-colors"
              >
                Start Membership
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#c8a672]/70">
          <span className="text-[10px] uppercase tracking-[0.4em]">Scroll</span>
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </div>
      </div>
    </section>
  );
};

// ============ Scroll-driven 3D scene sequence ============
// Six pinned frames that swap as the user scrolls — like the reference site.
// Each frame combines a full-viewport CSS "camera" (gradient + parallax layers)
// with a caption. Progress drives 3D transforms on the frames + camera zoom.
const SCENES = [
  {
    label: '01 · Chute',
    caption: 'It starts when the gate cracks open.',
    accent: '#c8a672',
    Icon: Clapperboard,
    grad: 'radial-gradient(ellipse at 30% 60%, #4a2a12 0%, #1a0d05 60%, #0f0b08 100%)',
    hint: 'Cameras rolling · countdown live',
  },
  {
    label: '02 · Live',
    caption: 'The run goes out — real-time, every angle, every screen.',
    accent: '#ff6b4a',
    Icon: Radio,
    grad: 'radial-gradient(ellipse at 70% 40%, #5a1a1a 0%, #2a0808 55%, #0f0505 100%)',
    hint: 'LIVE · phones, laptops, TVs paired',
    pulsing: true,
  },
  {
    label: '03 · Rewind',
    caption: 'Your run gets pulled, tagged, and slowed down.',
    accent: '#7ac5e6',
    Icon: ScanEye,
    grad: 'radial-gradient(ellipse at 40% 50%, #1a2a3a 0%, #0f1a2a 60%, #050a15 100%)',
    hint: 'Multi-camera timeline · 60fps',
  },
  {
    label: '04 · Breakdown',
    caption: 'A real coach marks the fix in ink, on your form.',
    accent: '#c8a672',
    Icon: Video,
    grad: 'radial-gradient(ellipse at 60% 45%, #3a1f0f 0%, #1f100a 60%, #0f0805 100%)',
    hint: 'Voice-over · lines on the swing',
  },
  {
    label: '05 · Drill',
    caption: "The exact reps to run this week. Nothing you don't need.",
    accent: '#8fd694',
    Icon: Play,
    grad: 'radial-gradient(ellipse at 50% 55%, #0a2a1a 0%, #05170e 60%, #020a05 100%)',
    hint: 'Drill list · saved to your phone',
  },
  {
    label: '06 · Next Weekend',
    caption: 'Same arena. Cleaner run. Photos already waiting.',
    accent: '#c8a672',
    Icon: Camera,
    grad: 'radial-gradient(ellipse at 45% 50%, #3a2a14 0%, #1a1005 60%, #0f0805 100%)',
    hint: 'Every ride tagged · download-ready',
  },
];

const ScrollSequence = () => {
  // No pin, no 600vh drag — just 3 cinematic scenes stacked at natural
  // scroll speed. Each one fades + rises when it enters the viewport.
  const scenes = SCENES.slice(0, 3);
  return (
    <section className="relative py-4 bg-[#0f0b08]">
      {scenes.map((s, i) => (
        <SceneCard key={s.label} scene={s} index={i} total={scenes.length} />
      ))}
    </section>
  );
};

const SceneCard = ({ scene: s, index, total }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold: 0.25 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const Icon = s.Icon;
  return (
    <div
      ref={ref}
      className="relative min-h-[70vh] flex items-center justify-center px-4 py-8"
    >
      <div
        className={`relative w-full max-w-6xl aspect-[16/9] rounded-3xl overflow-hidden border shadow-2xl transition-all duration-1000 ease-out ${
          visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-16 scale-95'
        }`}
        style={{
          background: s.grad,
          borderColor: `${s.accent}55`,
          boxShadow: `0 40px 100px -20px ${s.accent}55, 0 0 0 1px ${s.accent}22`,
        }}
      >
        <div
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_50%,rgba(0,0,0,0.7)_100%)]" />
        <div
          className={`absolute inset-0 flex items-center justify-center ${
            s.pulsing ? 'animate-pulse' : ''
          }`}
          style={{ color: s.accent }}
        >
          <Icon className="w-40 h-40 sm:w-56 sm:h-56 opacity-70" strokeWidth={0.9} />
        </div>
        <div
          className="absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.4em] flex items-center gap-3"
          style={{ color: s.accent }}
        >
          {s.pulsing && (
            <span className="relative flex h-2 w-2">
              <span
                className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                style={{ backgroundColor: s.accent }}
              />
              <span
                className="relative inline-flex rounded-full h-2 w-2"
                style={{ backgroundColor: s.accent }}
              />
            </span>
          )}
          REC · {s.label}
        </div>
        <div
          className="absolute top-6 right-6 font-mono text-[10px] uppercase tracking-[0.4em]"
          style={{ color: s.accent }}
        >
          {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12">
          <p
            className="uppercase tracking-[0.5em] text-[10px] mb-3"
            style={{ color: s.accent }}
          >
            Elevated Roping Media
          </p>
          <h3 className="font-display font-bold uppercase text-3xl sm:text-5xl lg:text-6xl leading-[0.95] text-[#f5efe6] max-w-3xl">
            {s.caption}
          </h3>
        </div>
      </div>
    </div>
  );
};

const PillarVisual = ({ index }) => {
  // Pure-CSS cinematic tiles — no external images so they never break.
  // Each tile: layered gradient background + big icon watermark + status pill.
  const themes = [
    {
      grad: 'from-[#5a1a1a] via-[#3a0f0f] to-[#0f0505]',
      accent: '#ff6b4a',
      Big: Radio,
      badgeIcon: Radio,
      badge: 'LIVE · Barrel Race Round 2',
      caption: 'Streaming right now',
      pulsing: true,
    },
    {
      grad: 'from-[#3a1f0f] via-[#5a2d14] to-[#1a0d05]',
      accent: '#c8a672',
      Big: Video,
      badgeIcon: Play,
      badge: 'Coach Breakdown · 6:41',
      caption: 'Voice-over + lines on your swing',
    },
    {
      grad: 'from-[#1a2a3a] via-[#0f1a2a] to-[#050a15]',
      accent: '#7ac5e6',
      Big: Camera,
      badgeIcon: Camera,
      badge: '412 photos · 38 videos tagged',
      caption: 'Filter to your name',
    },
    {
      grad: 'from-[#0a2a1a] via-[#05170e] to-[#020a05]',
      accent: '#8fd694',
      Big: Users,
      badgeIcon: Tv,
      badge: 'Now streaming on TV',
      caption: 'Pair with a 6-digit code',
    },
  ];
  const t = themes[index % 4];
  const Big = t.Big;
  const BadgeIcon = t.badgeIcon;
  return (
    <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-[#c8a672]/20 group">
      <div className={`absolute inset-0 bg-gradient-to-br ${t.grad}`} />
      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            'linear-gradient(rgba(200,166,114,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(200,166,114,0.15) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
      {/* Big icon watermark */}
      <div className="absolute inset-0 flex items-center justify-center opacity-30 group-hover:opacity-50 transition-opacity">
        <Big
          className="w-48 h-48 sm:w-64 sm:h-64"
          style={{ color: t.accent }}
          strokeWidth={0.8}
        />
      </div>
      {/* Corner tag */}
      <div
        className="absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.4em]"
        style={{ color: t.accent }}
      >
        {String(index + 1).padStart(2, '0')} / 04
      </div>
      {/* Bottom status pill */}
      <div className="absolute inset-x-6 bottom-6">
        <div className="flex items-center gap-3 mb-2">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              t.pulsing ? 'animate-pulse' : ''
            }`}
            style={{ backgroundColor: t.accent, color: '#0f0b08' }}
          >
            <BadgeIcon className="w-5 h-5" />
          </div>
          <span
            className="font-mono uppercase tracking-widest text-xs"
            style={{ color: t.accent }}
          >
            {t.badge}
          </span>
        </div>
        <p className="text-[#d9ccb8]/80 text-sm ml-13 pl-1">{t.caption}</p>
      </div>
      {/* Sheen on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-1000 bg-gradient-to-tr from-transparent via-white/5 to-transparent" />
    </div>
  );
};

const PricingCard = ({
  name,
  price,
  cadence,
  perks,
  cta,
  subtitle,
  highlight,
}) => (
  <div
    className={`relative rounded-3xl p-10 border transition-colors flex flex-col ${
      highlight
        ? 'border-[#c8a672] bg-gradient-to-br from-[#c8a672]/10 to-transparent shadow-[0_0_40px_-15px_rgba(200,166,114,0.6)]'
        : 'border-[#c8a672]/25 bg-[#1a120b]/40 hover:border-[#c8a672]/50'
    }`}
  >
    {highlight && (
      <span className="absolute -top-3 left-8 bg-[#c8a672] text-[#0f0b08] text-xs uppercase tracking-widest font-bold px-3 py-1 rounded-full">
        Best value
      </span>
    )}
    <h3 className="font-display font-bold uppercase text-3xl mb-2">{name}</h3>
    <div className="flex items-baseline gap-1 mb-1">
      <span className="font-display font-bold text-5xl">{price}</span>
      <span className="text-[#c8a672]">{cadence}</span>
    </div>
    {subtitle && (
      <p className="text-[#c8a672] text-sm mb-4">{subtitle}</p>
    )}
    <ul className="space-y-3 mt-6 mb-10 text-[#d9ccb8]">
      {perks.map((p) => (
        <li key={p} className="flex items-start gap-3">
          <Check className="w-4 h-4 mt-1 text-[#c8a672] flex-shrink-0" />
          <span>{p}</span>
        </li>
      ))}
    </ul>
    <Link
      to="/signup"
      data-testid={`draft-price-${name.toLowerCase()}`}
      className={`mt-auto group inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-bold uppercase tracking-widest text-sm transition-colors ${
        highlight
          ? 'bg-[#c8a672] text-[#0f0b08] hover:bg-[#d9bd88]'
          : 'border border-[#c8a672] text-[#c8a672] hover:bg-[#c8a672] hover:text-[#0f0b08]'
      }`}
    >
      {cta}
      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
    </Link>
  </div>
);

export default DraftHomePage;
